import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, AlertColor, Dialog, DialogActions, DialogContent, Divider, TextField } from '@mui/material';
import {
  fetchAdviseScoringRequests,
  postAdviseScoringInvites,
  postAdviseScoringReassign
} from '../../../../Api/Advise/ScoringRequests';
import { ScoringCategory, ScoringInvitee, ScoringRequestRow, ScoringSupplier } from '../../../../types/advise';
import { isNotFoundRefusal, refusalMessage } from '../../../../utils/refusalMessage';
import { DEFAULT_ADVANCE_BUTTON_STYLE, DEFAULT_CANCEL_BUTTON_STYLE } from '../../../../utils/styleHelpers';
import { TextBlock } from '../../../Atoms/Text';
import { TextButton } from '../../../Atoms/TextButton';
import { ComponentSpinner } from '../../Loading/ComponentSpinner';
import { ConfirmDialog } from '../ConfirmDialog';
import {
  alreadyInvited,
  emptyInvitee,
  fullName,
  InviteeDraft,
  inviteeOf,
  invitesToSend,
  repeatedEmails,
  toScoringInvitee
} from './RequestSupplierScoring.calc';
import { MAX_SCORING_INVITEES, SCORING_NOTE_LIMIT } from './scoringRequestLimits';
import { ScoringInviteeFields } from './ScoringInviteeFields';
import { ScoringRequestsSent } from './ScoringRequestsSent';
import { SubtitledDialogTitle } from './SubtitledDialogTitle';

const SEND_FAILED = "Couldn't send the scoring requests. Please try again.";
const REASSIGN_FAILED = "Couldn't reassign the request. Please try again.";
const OFF_SHORTLIST = 'That supplier is no longer on the shortlist.';

const notePlaceholder = (supplierName: string) =>
  `Included in every email, e.g. "Please score ${supplierName} during Thursday's demo."`;

const plural = (count: number, noun: string) => `${count} ${noun}${count === 1 ? '' : 's'}`;

const alreadyInvitedText = (rows: InviteeDraft[], supplierName: string) =>
  `${rows.map((row) => row.email.trim()).join(', ')} already ${rows.length === 1 ? 'has' : 'have'} an open request ` +
  `for ${supplierName} and won't be sent another.`;

interface RequestSupplierScoringDialogProps {
  engagementId: string;
  engagementName: string;
  supplier: ScoringSupplier;
  categories: ScoringCategory[];
  // For someone who can view the engagement but not edit it: the requests sent, with nothing to send or change.
  readOnly?: boolean;
  onClose: () => void;
  // Reloads the supplier cards, after a send or reassign and when the supplier has left the shortlist.
  onChanged: () => void;
}

/** Invites people outside the Portal to score one supplier, and lists and reassigns the requests already sent. */
export const RequestSupplierScoringDialog = ({
  engagementId,
  engagementName,
  supplier,
  categories,
  readOnly = false,
  onClose,
  onChanged
}: RequestSupplierScoringDialogProps) => {
  const [invitees, setInvitees] = useState<InviteeDraft[]>(() => [emptyInvitee()]);
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);
  const [notice, setNotice] = useState<{ severity: AlertColor; text: string } | null>(null);
  const [requests, setRequests] = useState<ScoringRequestRow[] | null>(null);
  const [listFailed, setListFailed] = useState(false);
  const [listLoading, setListLoading] = useState(true);
  const [duplicates, setDuplicates] = useState<InviteeDraft[] | null>(null);
  const toSend = invitesToSend(invitees);
  // The duplicate warning reads the list, so nothing is sent until it has loaded, or while it reloads after a change.
  const listReady = requests !== null && !listFailed && !listLoading;
  const repeated = repeatedEmails(invitees);
  const latestLoad = useRef(0);

  // A reload after a send or reassign can overtake a slower earlier load, which mustn't land on top of it.
  const loadRequests = useCallback(async () => {
    const load = ++latestLoad.current;
    setListFailed(false);
    setListLoading(true);
    const { data, error } = await fetchAdviseScoringRequests(engagementId, supplier.id);
    if (load !== latestLoad.current) return;
    setListLoading(false);
    if (error || !data) {
      setListFailed(true);
      return;
    }
    setRequests(data.requests);
  }, [engagementId, supplier.id]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const refused = (error: Parameters<typeof refusalMessage>[0], fallback: string) =>
    isNotFoundRefusal(error) ? OFF_SHORTLIST : refusalMessage(error, fallback);

  const updateInvitee = (rowId: number, patch: Partial<InviteeDraft>) =>
    setInvitees((rows) => rows.map((row) => (row.rowId === rowId ? { ...row, ...patch, error: undefined } : row)));

  const send = async (rows: InviteeDraft[]) => {
    setDuplicates(null);
    setSending(true);
    setNotice(null);
    const { data, error } = await postAdviseScoringInvites(
      engagementId,
      supplier.id,
      rows.map(toScoringInvitee),
      note.trim() || null
    );
    setSending(false);
    if (error || !data) {
      setNotice({ severity: 'error', text: refused(error, SEND_FAILED) });
      // A send that errors partway may already have sent some invitees, so reread the list before a retry.
      if (isNotFoundRefusal(error)) onChanged();
      else loadRequests();
      return;
    }

    const failures = new Map(
      data.results.filter((outcome) => !outcome.sent).map((outcome) => [outcome.email.toLowerCase(), outcome.error])
    );
    const kept = rows
      .filter((row) => failures.has(row.email.trim().toLowerCase()))
      .map((row) => ({ ...row, error: failures.get(row.email.trim().toLowerCase()) ?? undefined }));
    setInvitees(kept.length > 0 ? kept : [emptyInvitee()]);
    if (kept.length === 0) setNote('');
    setNotice(
      kept.length === 0
        ? { severity: 'success', text: `${plural(data.sent, 'scoring request')} sent.` }
        : {
            severity: data.sent > 0 ? 'warning' : 'error',
            text: `${data.sent} of ${data.results.length} sent. The ${kept.length === 1 ? 'invitee' : 'invitees'} below ${
              kept.length === 1 ? "wasn't" : "weren't"
            } emailed.`
          }
    );
    if (data.sent > 0) onChanged();
    loadRequests();
  };

  // Warned first, since the API skips anyone who already holds an unanswered request for the supplier.
  const requestSend = () => {
    if (!toSend) return;
    const already = alreadyInvited(toSend, requests ?? []);
    if (already.length === 0) send(toSend);
    else if (already.length < toSend.length) setDuplicates(already);
    else setNotice({ severity: 'error', text: alreadyInvitedText(already, supplier.supplier_name) });
  };

  const reassign = async (request: ScoringRequestRow, invitee: ScoringInvitee): Promise<string | null> => {
    const { data, error } = await postAdviseScoringReassign(engagementId, request.id, invitee);
    if (error || !data) {
      if (isNotFoundRefusal(error)) onChanged();
      return refused(error, REASSIGN_FAILED);
    }

    setNotice({ severity: 'success', text: `Reassigned to ${fullName(data.request)}.` });
    onChanged();
    loadRequests();
    return null;
  };

  // A new request to the same person; the failed one stays in the list as it was.
  const resend = async (request: ScoringRequestRow) => {
    setNotice(null);
    const { data, error } = await postAdviseScoringInvites(engagementId, supplier.id, [inviteeOf(request)], null);
    const outcome = data?.results[0];
    if (error || !outcome?.sent) {
      setNotice({ severity: 'error', text: outcome?.error ?? refused(error, SEND_FAILED) });
      if (isNotFoundRefusal(error)) return onChanged();
    } else {
      setNotice({ severity: 'success', text: `Sent ${fullName(request)} a new invite.` });
      onChanged();
    }
    loadRequests();
  };

  return (
    <Dialog
      open
      onClose={sending ? undefined : onClose}
      maxWidth="md"
      fullWidth>
      <SubtitledDialogTitle
        title={readOnly ? 'Scoring Requests' : 'Request Supplier Scoring'}
        subtitle={`${engagementName} · ${supplier.supplier_name}`}
        busy={sending}
        onClose={onClose}
      />
      <DialogContent
        dividers
        className="flex flex-col gap-4">
        {!readOnly && (
          <>
            <Alert
              severity="info"
              icon={false}>
              Scorers get a secure link by email. They need no Portal account, and they see only{' '}
              {supplier.supplier_name} and the categories you assign them.
            </Alert>
            {notice && <Alert severity={notice.severity}>{notice.text}</Alert>}
            <TextBlock
              size="xs"
              weight="semibold"
              color="grey6">
              INVITE SCORERS
            </TextBlock>
            {invitees.map((invitee) => (
              <ScoringInviteeFields
                key={invitee.rowId}
                invitee={invitee}
                categories={categories}
                disabled={sending}
                repeated={repeated.has(invitee.rowId)}
                onChange={(patch) => updateInvitee(invitee.rowId, patch)}
                onRemove={
                  invitees.length > 1
                    ? () => setInvitees((rows) => rows.filter((row) => row.rowId !== invitee.rowId))
                    : undefined
                }
              />
            ))}
            <TextButton
              className="self-start"
              disabled={sending || invitees.length >= MAX_SCORING_INVITEES}
              onClick={() => setInvitees((rows) => [...rows, emptyInvitee()])}>
              + Add another invitee
            </TextButton>
            <TextField
              label="Note (optional)"
              placeholder={notePlaceholder(supplier.supplier_name)}
              multiline
              minRows={2}
              size="small"
              value={note}
              disabled={sending}
              inputProps={{ maxLength: SCORING_NOTE_LIMIT }}
              onChange={(event) => setNote(event.target.value)}
            />
            <Divider />
          </>
        )}
        <ScoringRequestsSent
          requests={requests}
          failed={listFailed}
          onRetry={loadRequests}
          categories={categories}
          readOnly={readOnly}
          onReassign={reassign}
          onResend={resend}
        />
      </DialogContent>
      {!readOnly && (
        <DialogActions>
          <button
            type="button"
            className={DEFAULT_CANCEL_BUTTON_STYLE}
            disabled={sending}
            onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className={DEFAULT_ADVANCE_BUTTON_STYLE}
            disabled={!toSend || !listReady || sending}
            onClick={requestSend}>
            {sending ? <ComponentSpinner sx={{ height: '19px' }} /> : 'Send Requests'}
          </button>
        </DialogActions>
      )}
      <ConfirmDialog
        open={!!duplicates}
        title="Already invited"
        message={`${alreadyInvitedText(duplicates ?? [], supplier.supplier_name)} Send the others?`}
        confirmLabel="Send"
        confirmColor="primary"
        onConfirm={() => (toSend ? send(toSend.filter((row) => !duplicates?.includes(row))) : undefined)}
        onCancel={() => setDuplicates(null)}
      />
    </Dialog>
  );
};
